import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

interface LineItem {
  name: string;
  amount: number;
}

interface QuoteData {
  id: string;
  public_token: string;
  lead_id: string;
  line_items: LineItem[];
  subtotal: number;
  btw: number;
  total: number;
  note?: string;
  status: string;
  leads: {
    firstname: string;
    lastname: string;
    email?: string;
  };
}

const MOCK_QUOTE: QuoteData = {
  id: 'mock',
  public_token: 'mock-token',
  lead_id: 'mock-lead',
  line_items: [
    { name: 'Houtlook Premium PVC — 42 m² x €42.95/m²', amount: 1803.90 },
    { name: 'Ondervloer egaliseren', amount: 185.00 },
  ],
  subtotal: 1988.90,
  btw: 417.67,
  total: 2406.57,
  note: '',
  status: 'sent',
  leads: { firstname: 'Jan', lastname: 'de Vries' },
};

export default function QuoteSigningPage() {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<QuoteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setLoading(false); setNotFound(true); return; }

    if (!isSupabaseConfigured) {
      setQuote(MOCK_QUOTE);
      setName(`${MOCK_QUOTE.leads.firstname} ${MOCK_QUOTE.leads.lastname}`);
      setLoading(false);
      return;
    }

    supabase
      .from('quotes')
      .select('id, public_token, lead_id, line_items, subtotal, btw, total, note, status, leads(firstname, lastname, email)')
      .eq('public_token', token)
      .single()
      .then(({ data, error: err }) => {
        if (err || !data) {
          setNotFound(true);
        } else {
          const q = data as unknown as QuoteData;
          setQuote(q);
          setName(`${q.leads?.firstname || ''} ${q.leads?.lastname || ''}`.trim());
          if (q.status === 'signed') setSigned(true);
        }
        setLoading(false);
      });
  }, [token]);

  const handleSign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !accepted || !token) return;
    setSigning(true);
    setError(null);

    if (!isSupabaseConfigured) {
      await new Promise(r => setTimeout(r, 800));
      setSignedName(name.trim());
      setSigned(true);
      setSigning(false);
      return;
    }

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/sign_quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': anonKey },
        body: JSON.stringify({
          action: 'sign',
          token,
          signed_name: name.trim(),
          signed_user_agent: navigator.userAgent,
        }),
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        setError(json.error || 'Er is een fout opgetreden. Probeer het opnieuw.');
      } else {
        setSignedName(name.trim());
        setSigned(true);
      }
    } catch {
      setError('Er is een fout opgetreden. Controleer uw verbinding en probeer het opnieuw.');
    } finally {
      setSigning(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F0EA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 32, height: 32, border: '2px solid #C8BDB0', borderTopColor: '#6B5E55', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────────
  if (notFound || !quote) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F0EA', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <p style={{ fontFamily: 'Georgia, serif', fontSize: 20, fontWeight: 600, color: '#1C1917', marginBottom: 8 }}>Offerte niet gevonden</p>
          <p style={{ fontSize: 14, color: '#8A7E75' }}>Deze link is ongeldig of verlopen. Neem contact op met uw adviseur.</p>
        </div>
      </div>
    );
  }

  // ── Signed confirmation ──────────────────────────────────────────────────────
  if (signed) {
    const displayName = signedName || name;
    return (
      <div style={{ minHeight: '100vh', background: '#F5F0EA', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div style={{ maxWidth: 480, width: '100%', background: '#fff', borderRadius: 16, padding: '2.5rem 2rem', boxShadow: '0 1px 4px rgba(28,25,23,0.08)', border: '1px solid #E8E0D5', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(61,124,61,0.10)', border: '1px solid rgba(61,124,61,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3D7C3D" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <p style={{ fontFamily: 'Georgia, serif', fontSize: 20, fontWeight: 600, color: '#1C1917', marginBottom: 10 }}>Bedankt {displayName}.</p>
          <p style={{ fontSize: 14, color: '#6B5E55', lineHeight: 1.65, marginBottom: 0 }}>
            Uw akkoord is ontvangen. Uw contactpersoon neemt spoedig contact met u op om de volgende stappen te bespreken.
          </p>
          <div style={{ marginTop: '1.75rem', paddingTop: '1.25rem', borderTop: '1px solid #E8E0D5' }}>
            <p style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#A09080', margin: 0 }}>PVC Vloer Nederland</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Main signing page ────────────────────────────────────────────────────────
  const lineItems: LineItem[] = Array.isArray(quote.line_items) ? quote.line_items : [];

  return (
    <div style={{ minHeight: '100vh', background: '#EDE8E0', padding: '2.5rem 1rem 4rem', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>

        {/* Wordmark */}
        <div style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#A09080', margin: 0, fontWeight: 600 }}>PVC Vloer Nederland</p>
        </div>

        <div style={{ background: '#F5F0EA', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 6px rgba(28,25,23,0.08)', border: '1px solid #E8E0D5' }}>

          {/* Header */}
          <div style={{ padding: '1.75rem 2rem 1.5rem', borderBottom: '1px solid #E8E0D5' }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#A09080' }}>Prijsindicatie</p>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 22, fontWeight: 600, color: '#1C1917', margin: '0 0 2px' }}>
              {quote.leads?.firstname} {quote.leads?.lastname}
            </h1>
            <p style={{ margin: 0, fontSize: 13, color: '#8A7E75' }}>Geldig gedurende 90 dagen na ontvangst</p>
          </div>

          {/* Line items */}
          <div style={{ padding: '1.25rem 2rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {lineItems.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #E8E0D5' }}>
                    <td style={{ padding: '10px 0', fontSize: 14, color: '#3D3530' }}>{item.name}</td>
                    <td style={{ padding: '10px 0', fontSize: 14, color: '#3D3530', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      &euro;{Number(item.amount).toFixed(2)}
                    </td>
                  </tr>
                ))}
                {lineItems.length === 0 && (
                  <tr>
                    <td colSpan={2} style={{ padding: '10px 0', fontSize: 14, color: '#8A7E75', fontStyle: 'italic' }}>Geen regelitems</td>
                  </tr>
                )}
                <tr>
                  <td style={{ padding: '14px 0 6px', fontSize: 13, color: '#8A7E75' }}>Subtotaal excl. BTW</td>
                  <td style={{ padding: '14px 0 6px', fontSize: 13, color: '#8A7E75', textAlign: 'right' }}>
                    &euro;{Number(quote.subtotal).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 0', fontSize: 12, color: '#A09080' }}>BTW (21%)</td>
                  <td style={{ padding: '4px 0', fontSize: 12, color: '#A09080', textAlign: 'right' }}>
                    &euro;{Number(quote.btw).toFixed(2)}
                  </td>
                </tr>
                <tr style={{ borderTop: '1px solid #E8E0D5' }}>
                  <td style={{ padding: '12px 0 0', fontSize: 15, fontWeight: 600, color: '#1C1917' }}>Totaal incl. BTW</td>
                  <td style={{ padding: '12px 0 0', fontSize: 20, fontWeight: 700, color: '#3D7C3D', textAlign: 'right' }}>
                    &euro;{Number(quote.total).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>

            {quote.note && (
              <p style={{ marginTop: '1rem', fontSize: 13, color: '#8A7E75', fontStyle: 'italic', lineHeight: 1.55 }}>{quote.note}</p>
            )}
          </div>

          {/* Signing form */}
          <div style={{ padding: '0 2rem 2rem', borderTop: '1px solid #E8E0D5' }}>
            <form onSubmit={handleSign}>
              <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8A7E75', marginBottom: 6 }}>
                  Uw naam
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  placeholder="Voor- en achternaam"
                  style={{ width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 8, border: '1px solid #D4C9BE', background: '#FDFAF7', color: '#1C1917', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: '1.25rem' }}>
                <div
                  onClick={() => setAccepted(v => !v)}
                  style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${accepted ? '#3D7C3D' : '#C8BDB0'}`, background: accepted ? '#3D7C3D' : 'transparent', flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s' }}
                >
                  {accepted && (
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="1.5 5 4 7.5 8.5 2.5" />
                    </svg>
                  )}
                </div>
                <span style={{ fontSize: 13, color: '#6B5E55', lineHeight: 1.55 }}>
                  Ik ga akkoord met deze prijsindicatie en de algemene voorwaarden van PVC Vloer Nederland.
                </span>
              </label>

              {error && (
                <p style={{ fontSize: 13, color: '#B91C1C', background: 'rgba(185,28,28,0.06)', border: '1px solid rgba(185,28,28,0.15)', borderRadius: 8, padding: '8px 12px', marginBottom: '1rem' }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={signing || !name.trim() || !accepted}
                style={{ width: '100%', padding: '12px 0', fontSize: 14, fontWeight: 600, borderRadius: 10, border: 'none', background: '#3D7C3D', color: '#fff', cursor: signing || !name.trim() || !accepted ? 'not-allowed' : 'pointer', opacity: signing || !name.trim() || !accepted ? 0.5 : 1, transition: 'opacity 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                {signing ? (
                  <>
                    <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Bezig met ondertekenen...
                  </>
                ) : 'Akkoord en ondertekenen'}
              </button>
            </form>
          </div>

        </div>

        <p style={{ marginTop: '1.5rem', fontSize: 11, color: '#A09080', textAlign: 'center' }}>
          PVC Vloer Nederland B.V.
        </p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
